// Size buckets (I-1a Batch 3 decision 2, threat model I1 / data map). Plaintext
// is padded to a fixed step before encryption, so the ciphertext length does not
// tell a long free-text answer from a single chip.
//
// Layout: [4-byte big-endian content length][content][zero bytes up to the step].
// Records use 1 / 2 / 4 / 8 / 16 / 32 / 64 KiB, then whole 64 KiB steps; photo
// blobs use 64 KiB steps only. The padding sits inside the AES-GCM ciphertext,
// so it is authenticated like the content.

const KiB = 1024
const LENGTH_PREFIX = 4
const MAX_CONTENT = 0xffffffff

export const RECORD_SIZE_STEPS: readonly number[] = [1, 2, 4, 8, 16, 32, 64].map((k) => k * KiB)
export const LARGE_STEP = 64 * KiB

export type SizeRule = (contentLength: number) => number

/** Padded size for a diary record. */
export const recordSize: SizeRule = (contentLength) => {
  const needed = contentLength + LENGTH_PREFIX
  for (const step of RECORD_SIZE_STEPS) {
    if (needed <= step) {
      return step
    }
  }
  return Math.ceil(needed / LARGE_STEP) * LARGE_STEP
}

/** Padded size for a photo blob: whole 64 KiB steps. */
export const blobSize: SizeRule = (contentLength) =>
  Math.max(1, Math.ceil((contentLength + LENGTH_PREFIX) / LARGE_STEP)) * LARGE_STEP

export function pad(content: Uint8Array, sizeRule: SizeRule = recordSize): Uint8Array<ArrayBuffer> {
  if (content.length > MAX_CONTENT) {
    throw new Error('content too large to pad')
  }
  const out = new Uint8Array(sizeRule(content.length))
  new DataView(out.buffer).setUint32(0, content.length)
  out.set(content, LENGTH_PREFIX)
  return out
}

export function unpad(padded: Uint8Array): Uint8Array<ArrayBuffer> {
  if (padded.length < LENGTH_PREFIX) {
    throw new Error('padded content too short')
  }
  const view = new DataView(padded.buffer, padded.byteOffset, padded.byteLength)
  const length = view.getUint32(0)
  const end = LENGTH_PREFIX + length
  if (end > padded.length) {
    throw new Error('padded length out of range')
  }
  for (let i = end; i < padded.length; i++) {
    if (padded[i] !== 0) {
      throw new Error('padding is not zero')
    }
  }
  return padded.slice(LENGTH_PREFIX, end)
}
